import { Controller } from '@hotwired/stimulus'

const FORMATS = ['badge', 'rating', 'carousel', 'wall']
const THEMES = ['light', 'dark']

// Должен совпадать с ClubWidgetPreview::SLUG_PLACEHOLDER: превью показывает
// клуб-витрину, а копируемый сниппет — плейсхолдер под клуб читателя.
const SLUG_PLACEHOLDER = 'YOUR-CLUB-SLUG'

// Акцент красит саму марку Padelmesh (она в currentColor), поэтому классы text-*.
// Цвета — литералы из ACCENTS в widget/index.js, а не токены темы: превью
// показывает виджет на сайте клуба и не должно инвертироваться вместе с нашей
// темой. Значения обязаны совпадать с mark_accent в _widget_preview_frame.slim.
const ACCENTS = {
  'cobalt': 'text-[#4177ff]',
  'orange': 'text-[#ff5734]',
  'deep-blue': 'text-[#2d3eca]'
}

// Сниппет — это HTML, который клуб вставит к себе как есть: имя клуба и
// подписи экранируем, иначе «Padel & Co» дал бы невалидную разметку.
const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[char]
  )

export default class WidgetKitController extends Controller {
  static targets = [
    'accent',
    'carouselDot',
    'carouselSlide',
    'code',
    'frame',
    'list',
    'noResults',
    'option',
    'query',
    'seoLink',
    'suggestions',
    'themeOption',
    'themed',
    'variant'
  ]

  static values = { suggestionsUrl: String, pageUrl: String }

  connect() {
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.closeSuggestions()
    }
    document.addEventListener('click', this.onDocumentClick)

    this.format =
      this.pressedValue(this.optionTargets, 'widgetKitFormatParam') || 'badge'
    this.accentName =
      this.pressedValue(this.accentTargets, 'widgetKitAccentParam') || 'cobalt'
    this.themeName =
      this.pressedValue(this.themeOptionTargets, 'widgetKitThemeParam') ||
      'light'
    this.render()
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this.searchTimer)
  }

  select(event) {
    this.format = event.params.format
    this.render()
  }

  accent(event) {
    this.accentName = event.params.accent
    this.render()
  }

  theme(event) {
    this.themeName = event.params.theme
    this.render()
  }

  // Перелистывание карусели в превью. Состояние не храним: текущий слайд — тот,
  // что без hidden. Перезагрузка турбо-фрейма (смена клуба) приносит разметку
  // с первым видимым слайдом, и карусель сама оказывается в начальном положении.
  prevReview() {
    this.showReview(this.currentReviewIndex() - 1)
  }

  nextReview() {
    this.showReview(this.currentReviewIndex() + 1)
  }

  goToReview(event) {
    this.showReview(event.params.index)
  }

  currentReviewIndex() {
    const index = this.carouselSlideTargets.findIndex(
      (slide) => !slide.classList.contains('hidden')
    )
    return index < 0 ? 0 : index
  }

  showReview(target) {
    const count = this.carouselSlideTargets.length
    if (count < 2) return

    const index = ((target % count) + count) % count
    this.carouselSlideTargets.forEach((slide, slideIndex) => {
      slide.classList.toggle('hidden', slideIndex !== index)
    })
    // Точки красятся адаптивными парами классов — та же пара, что в разметке
    // кадра (_widget_preview_frame.slim).
    this.carouselDotTargets.forEach((dot, dotIndex) => {
      const on = dotIndex === index
      dot.classList.toggle('bg-black', on)
      dot.classList.toggle('widget-dark:bg-white', on)
      dot.classList.toggle('bg-black/20', !on)
      dot.classList.toggle('widget-dark:bg-white/30', !on)
      if (on) dot.setAttribute('aria-current', 'true')
      else dot.removeAttribute('aria-current')
    })
  }

  // Подсказки — общий эндпоинт поиска с context=club: только клубы, без квоты
  // городам и тренерам. Дребезг и отбрасывание устаревших ответов — как в
  // tournament_club_controller: медленный ответ не должен перерисовать выдачу
  // поверх результатов более позднего ввода.
  search() {
    clearTimeout(this.searchTimer)
    // Поколение двигаем на каждый ввод, а не только в fetchSuggestions: иначе
    // ответ запроса, ушедшего до правки поля, проходил проверку и открывал
    // список заново — с подсказками под уже стёртый запрос.
    this.lastRequestId = (this.lastRequestId || 0) + 1
    const query = this.queryTarget.value.trim()
    if (query.length < 2) {
      this.closeSuggestions()
      return
    }
    this.searchTimer = setTimeout(() => this.fetchSuggestions(query), 300)
  }

  async fetchSuggestions(query) {
    const requestId = (this.lastRequestId = (this.lastRequestId || 0) + 1)
    try {
      const url = `${this.suggestionsUrlValue}&q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { Accept: 'application/json' }
      })
      if (requestId !== this.lastRequestId || !response.ok) return

      const data = await response.json()
      if (requestId !== this.lastRequestId) return

      this.renderSuggestions(data.items || [])
    } catch {
      // Только если запрос всё ещё актуален: closeSuggestions двигает
      // поколение, и упавший старый запрос иначе отменял бы новый, живой —
      // его подсказки не отрисовались бы вообще.
      if (requestId === this.lastRequestId) this.closeSuggestions()
    }
  }

  // Пустая выдача не закрывает список: клуба может не быть в базе, и тогда
  // единственный полезный ответ — ссылка на форму заявки. Она серверная и лежит
  // в разметке, чтобы не тащить сюда ни текст, ни адрес.
  renderSuggestions(clubs) {
    this.listTarget.innerHTML = ''
    clubs.forEach((club) => {
      const item = document.createElement('button')
      item.type = 'button'
      item.className =
        'block w-full px-4 py-3 text-left text-r-14 text-text-primary hover:bg-btn-gray-overlay-low'
      item.textContent = [club.name, club.subtitle].filter(Boolean).join(' · ')
      item.addEventListener('click', () => this.pick(club))
      this.listTarget.appendChild(item)
    })

    this.noResultsTarget.classList.toggle('hidden', clubs.length > 0)
    this.suggestionsTarget.classList.remove('hidden')
  }

  pick(club) {
    const slug = club.slug
    if (!slug) return

    this.queryTarget.value = club.name
    this.closeSuggestions()

    // Превью и кнопки форматов отдаёт отдельный action — не всю /for-clubs.
    // Формат/тему/акцент восстановит render() по turbo:frame-load. Slug тут
    // не запоминается: сниппет читает slug, URL и текст из узла seoLink в
    // кадре, поэтому они меняются атомарно, а при сбое загрузки кадра код
    // целиком остаётся про прошлый клуб — рассинхрона состояний не бывает.
    if (this.hasFrameTarget) {
      this.frameTarget.src = `${this.pageUrlValue}?club=${encodeURIComponent(slug)}`
    }
  }

  closeSuggestions() {
    // Закрытие отменяет и отложенный вызов, и запрос в полёте: клик мимо
    // списка не должен оборачиваться тем, что список сам откроется через
    // полсекунды. Таймер — для клика внутри debounce, поколение — для клика
    // после того, как запрос уже ушёл.
    clearTimeout(this.searchTimer)
    this.lastRequestId = (this.lastRequestId || 0) + 1
    if (!this.hasSuggestionsTarget) return

    this.suggestionsTarget.classList.add('hidden')
    this.listTarget.innerHTML = ''
    this.noResultsTarget.classList.add('hidden')
  }

  pressedValue(targets, datasetKey) {
    return targets.find(
      (target) => target.getAttribute('aria-pressed') === 'true'
    )?.dataset[datasetKey]
  }

  render() {
    const format = FORMATS.includes(this.format) ? this.format : 'badge'
    const theme = THEMES.includes(this.themeName) ? this.themeName : 'light'
    const accent = ACCENTS[this.accentName] ? this.accentName : 'cobalt'

    // Вариант widget-dark в tailwind.css цепляется за этот атрибут.
    this.element.dataset.widgetTheme = theme

    this.variantTargets.forEach((variant) => {
      variant.classList.toggle(
        'hidden',
        variant.dataset.widgetKitVariantParam !== format
      )
    })

    this.themedTargets.forEach((element) => {
      element.classList.remove(...Object.values(ACCENTS))
      element.classList.add(ACCENTS[accent])
    })

    this.togglePressed(this.optionTargets, 'widgetKitFormatParam', format, true)
    this.togglePressed(
      this.themeOptionTargets,
      'widgetKitThemeParam',
      theme,
      true
    )
    this.togglePressed(
      this.accentTargets,
      'widgetKitAccentParam',
      accent,
      false
    )

    this.updateCode(format, theme, accent)
  }

  // Кнопки формата и темы перекрашиваются целиком, у кружков акцента меняется
  // только обводка — свой цвет они обязаны сохранить.
  togglePressed(targets, datasetKey, value, fill) {
    targets.forEach((target) => {
      const isSelected = target.dataset[datasetKey] === value
      target.setAttribute('aria-pressed', String(isSelected))

      if (fill) {
        target.classList.toggle('border-brand-cobalt', isSelected)
        target.classList.toggle('bg-brand-cobalt', isSelected)
        target.classList.toggle('text-white', isSelected)
        target.classList.toggle('bg-bg-surface', !isSelected)
        target.classList.toggle('text-text-primary', !isSelected)
      } else {
        target.classList.toggle('border-text-primary', isSelected)
      }

      target.classList.toggle('border-transparent', !isSelected)
    })
  }

  updateCode(format, theme, accent) {
    if (!this.hasCodeTarget) return

    // Ссылка в сниппете — обычный HTML рядом с дивом виджета: поисковики
    // видят её на сайте клуба без JS (SEO-смысл виджета), а загрузчик,
    // отрисовав карточку в shadow DOM, визуально её заменяет. Slug, URL и
    // текст считает сервер и привозит кадром превью одним узлом: путь
    // профиля из slug тут не собрать — в нём страна и город.
    const seo = this.hasSeoLinkTarget ? this.seoLinkTarget.dataset : {}
    const slug = seo.slug || SLUG_PLACEHOLDER
    const url = escapeHtml(seo.url || 'https://padelmesh.com/')
    const text = escapeHtml(seo.text || 'Padelmesh')

    // Дефолт должен совпадать с widget_code в for_clubs.slim — он страхует
    // случай, когда JS не поднялся.
    this.codeTarget.textContent = [
      '<div class="padelmesh-widget"',
      `     data-club="${slug}"`,
      `     data-format="${format}"`,
      `     data-theme="${theme}"`,
      `     data-accent="${accent}">`,
      `  <a href="${url}" target="_blank" rel="noopener">${text}</a>`,
      '</div>',
      '<script src="https://padelmesh.com/widget.js" async></script>'
    ].join('\n')
  }
}
